import { Target, Eye, Heart, Award, Users, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";

const VALUES = [
  { icon: Award, title: "Quality First", desc: "Pharmaceutical-grade discipline in every batch, every shipment, every time." },
  { icon: Sparkles, title: "Scientific Integrity", desc: "Formulations grounded in veterinary science, validated through field trials." },
  { icon: Users, title: "Partner Mindset", desc: "We grow when our distributors, brands and farmers grow." },
  { icon: Heart, title: "Animal Welfare", desc: "Healthier animals build healthier communities and livelihoods." },
];

export default function About() {
  return (
    <>
      <SEO title="About Us" description="Forage is a Saharanpur-based veterinary pharmaceutical manufacturer combining scientific formulation, pharma-grade production and partner-first values." path="/about" />

      {/* Header */}
      <section className="bg-[#0B2545] text-white py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-fr relative">
          <span className="overline text-[#2A9D8F]">About Forage</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 max-w-3xl leading-tight">
            Building India's most trusted veterinary pharmaceutical company.
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-2xl">
            We engineer science-backed animal health products and manufacture
            them with the discipline of human pharmaceuticals.
          </p>
        </div>
      </section>

      {/* Profile */}
      <section className="section-padding bg-white">
        <div className="container-fr grid lg:grid-cols-2 gap-14 items-center">
          <img src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1200&q=80" alt="Forage facility" loading="lazy" className="rounded-xl object-cover w-full h-[500px] shadow-lg" />
          <div>
            <span className="overline">Company Profile</span>
            <h2 className="h-section mt-3">A modern manufacturer with veterinary DNA.</h2>
            <p className="text-slate-600 mt-6 leading-relaxed">
              Forage Animal Health & Nutrition Pvt. Ltd. was founded in
              Saharanpur, Uttar Pradesh with one belief — that veterinary
              products deserve the same scientific rigour and manufacturing
              discipline as human pharmaceuticals.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Today, our integrated facility produces over 50 formulations
              spanning liquid supplements, mineral premixes, feed additives,
              nutritional injectables (oral grade) and herbal therapeutics —
              serving distributors, brands and institutions across 18+
              countries.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              From farm to facility, from formulation to fulfilment — every
              link in our chain is engineered for trust.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-fr grid md:grid-cols-2 gap-8">
          <div data-testid="vision-card" className="bg-white p-10 rounded-xl border border-slate-200 shadow-sm">
            <Eye className="text-[#2A9D8F]" size={32} />
            <h3 className="font-display text-2xl font-bold text-[#0B2545] mt-4">Our Vision</h3>
            <p className="text-slate-600 mt-4 leading-relaxed">
              To be the most trusted Indian manufacturer of veterinary
              pharmaceuticals and animal nutrition — recognised globally for
              scientific innovation, manufacturing integrity and partner
              outcomes.
            </p>
          </div>
          <div data-testid="mission-card" className="bg-white p-10 rounded-xl border border-slate-200 shadow-sm">
            <Target className="text-[#2A9D8F]" size={32} />
            <h3 className="font-display text-2xl font-bold text-[#0B2545] mt-4">Our Mission</h3>
            <p className="text-slate-600 mt-4 leading-relaxed">
              To improve animal health and productivity worldwide by
              manufacturing pharmaceutical-grade veterinary products with
              uncompromising quality, scientific formulation and reliable
              supply.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="container-fr">
          <div className="max-w-3xl">
            <span className="overline">Core Values</span>
            <h2 className="h-section mt-3">Four principles. One culture.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {VALUES.map((v, i) => (
              <div key={v.title} className="p-7 rounded-xl border border-slate-200 card-lift fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-11 h-11 rounded-lg bg-[#0B2545] text-[#2A9D8F] flex items-center justify-center mb-5">
                  <v.icon size={22} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#0B2545]">{v.title}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing philosophy */}
      <section className="section-padding bg-[#0B2545] text-white">
        <div className="container-fr max-w-4xl">
          <span className="overline text-[#2A9D8F]">Manufacturing Philosophy</span>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 leading-tight">
            "If we wouldn't accept this batch for our own farm, it doesn't leave our facility."
          </h2>
          <p className="text-slate-300 mt-8 leading-relaxed text-lg">
            Every formulation begins with a question — what does this animal
            actually need? Our scientists, formulators and QC teams answer that
            question with field data, raw material specs and laboratory
            evidence. The result is a product portfolio that performs in real
            barns, real clinics and real export shipments.
          </p>
        </div>
      </section>
    </>
  );
}
